/**
 * Pure helpers for the "Add custom server" dialog and the OAuth round trip of
 * custom MCP servers on the MCP Servers tab.
 *
 * Flow: the dialog POSTs `auth_type: 'mcp_oauth'`; central-api discovers the
 * provider, registers a client and answers with an `authorize_url`. The user
 * confirms and the browser leaves for the provider. central-api's callback
 * sends it back to `/integrations?tab=governance&mcp_oauth=…`, which the tab
 * reads once, reports as a toast and strips from the URL.
 */
import { CENTRAL_API_BASE_URL } from '../../lib/constants/central-api';
import { CreateMcpConnectionRequest } from '../../lib/api/control-center/control-center-api';

// ---------- the add-custom form ----------

/** Authentication choices the dialog offers for a custom server. */
export type CustomAuthChoice = 'none' | 'bearer' | 'mcp_oauth';

export interface CustomForm {
  name: string;
  url: string;
  auth_type: CustomAuthChoice;
  /** Bearer secret. */
  auth_secret: string;
  // OAuth "Advanced" fields — only needed when the server has no dynamic
  // client registration.
  oauth_client_id: string;
  oauth_client_secret: string;
  oauth_scopes: string;
}

export const emptyCustomForm: CustomForm = {
  name: '',
  url: '',
  auth_type: 'none',
  auth_secret: '',
  oauth_client_id: '',
  oauth_client_secret: '',
  oauth_scopes: '',
};

/**
 * Client-side validation. Returns the i18n key of the first problem, or
 * `null` when the form can be sent. OAuth tokens never travel over plain
 * http, so an OAuth server must be https (central-api refuses it too).
 */
export const validateCustomForm = (form: CustomForm): string | null => {
  const name = form.name.trim();
  const url = form.url.trim();
  if (!name) return 'ControlCenterNameRequired';
  if (!/^https?:\/\/.+/i.test(url)) return 'ControlCenterUrlInvalid';
  if (form.auth_type === 'mcp_oauth' && !/^https:\/\/.+/i.test(url)) {
    return 'ControlCenterOauthHttpsRequired';
  }
  if (form.auth_type === 'bearer' && !form.auth_secret.trim()) {
    return 'ControlCenterSecretRequired';
  }
  return null;
};

/**
 * The POST body for a custom server. None and Bearer produce exactly the
 * payload the dialog has always sent; OAuth adds only the optional client
 * fields the user filled in.
 */
export const buildCustomConnectionPayload = (
  form: CustomForm
): CreateMcpConnectionRequest => {
  const base: CreateMcpConnectionRequest = {
    name: form.name.trim(),
    type: 'custom',
    url: form.url.trim(),
    auth_type: form.auth_type,
  };

  if (form.auth_type === 'bearer') {
    return { ...base, auth_secret: form.auth_secret };
  }

  if (form.auth_type === 'mcp_oauth') {
    const payload: CreateMcpConnectionRequest = { ...base };
    const clientId = form.oauth_client_id.trim();
    const scopes = form.oauth_scopes.trim().split(/\s+/).filter(Boolean);
    if (clientId) payload.oauth_client_id = clientId;
    if (form.oauth_client_secret.trim()) {
      payload.oauth_client_secret = form.oauth_client_secret.trim();
    }
    if (scopes.length > 0) payload.oauth_scopes = scopes.join(' ');
    return payload;
  }

  return base;
};

// ---------- redirect URI ----------

const OAUTH_CALLBACK_PATH = 'mcp-connections/oauth/callback';

/**
 * The redirect URI to register at a provider that needs a manual client:
 * central-api's public callback, `<api base>/api/v1/mcp-connections/oauth/callback`.
 * `CENTRAL_API_BASE_URL` already ends in `/api/v1/`; a base without the
 * trailing slash is handled too.
 */
export const buildMcpOauthRedirectUri = (
  base: string = CENTRAL_API_BASE_URL
): string => `${base.endsWith('/') ? base : `${base}/`}${OAUTH_CALLBACK_PATH}`;

// ---------- the authorize redirect ----------

/**
 * Checks the URL central-api returned before the browser is sent there. Only
 * an absolute https URL is followed. `host` is where the browser actually
 * goes — the confirmation names it, so it can never differ from the
 * destination; `authorization_server_host` is only the fallback label.
 */
export const resolveAuthorizeRedirect = (
  authorizeUrl: string | null | undefined,
  authorizationServerHost?: string | null
): { url: string; host: string } | null => {
  if (typeof authorizeUrl !== 'string' || !authorizeUrl.trim()) return null;
  let parsed: URL;
  try {
    parsed = new URL(authorizeUrl.trim());
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  return {
    url: parsed.toString(),
    host: parsed.host || authorizationServerHost || '',
  };
};

// ---------- the return from the provider ----------

/** Query params central-api's callback appends on the way back. */
export const MCP_OAUTH_RETURN_PARAMS = ['mcp_oauth', 'reason', 'connection'];

export type McpOauthReturn =
  | { outcome: 'connected'; connectionId: string | null }
  | { outcome: 'error'; reason: string; connectionId: string | null };

/**
 * Reads the callback's result from the page URL, or `null` when the URL does
 * not carry one (or carries a value this client does not know).
 */
export const parseMcpOauthReturn = (
  params: URLSearchParams
): McpOauthReturn | null => {
  const outcome = params.get('mcp_oauth');
  const rawId = params.get('connection');
  const connectionId = rawId && /^\d+$/.test(rawId) ? rawId : null;
  if (outcome === 'connected') return { outcome: 'connected', connectionId };
  if (outcome === 'error') {
    return {
      outcome: 'error',
      reason: params.get('reason') || 'server_error',
      connectionId,
    };
  }
  return null;
};

/** A copy of `params` without the callback's result params. */
export const stripMcpOauthReturnParams = (
  params: URLSearchParams
): URLSearchParams => {
  const next = new URLSearchParams(params);
  MCP_OAUTH_RETURN_PARAMS.forEach((key) => next.delete(key));
  return next;
};

const ERROR_MESSAGE_KEYS: Record<string, string> = {
  denied: 'ControlCenterOauthErrorDenied',
  state_rejected: 'ControlCenterOauthErrorStateRejected',
  connection_not_found: 'ControlCenterOauthErrorConnectionNotFound',
  token_exchange_failed: 'ControlCenterOauthErrorTokenExchange',
  introspection_failed: 'ControlCenterOauthErrorIntrospection',
  reauth_required: 'ControlCenterOauthErrorReauth',
  server_error: 'ControlCenterOauthErrorGeneric',
};

/**
 * The i18n key of the message for a callback error code. The raw `reason`
 * from the URL is never shown: anyone can craft that link.
 */
export const mcpOauthErrorMessageKey = (reason: string): string =>
  Object.prototype.hasOwnProperty.call(ERROR_MESSAGE_KEYS, reason)
    ? ERROR_MESSAGE_KEYS[reason]
    : 'ControlCenterOauthErrorGeneric';
